import { useFormikContext } from 'formik'
import React from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import * as Api from 'src/api'
import PortfolioIcon from 'src/assets/icons/customIcons/menu-icons/Portfolio'
import { useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useLocale } from 'src/hooks/locale/locale'
import { useTranslatable } from 'src/hooks/locale/utils'
import { Button } from 'src/tailwind/components/Button'
import { Section } from 'src/tailwind/components/Section'
import BackButton from 'src/views/components/BackButton'
import { Form } from 'src/views/components/forms/formik/Form'
import { FormError } from 'src/views/components/forms/formik/FormError'
import SelectInput from 'src/views/components/forms/formik/SelectInput'
import TextInput from 'src/views/components/forms/formik/TextInput'
import SuspenseWrapper from 'src/views/includes/SuspenseWrapper'

interface FormikValues {
  readonly sectionId: string
  readonly semester: string
  readonly allowed: string
  readonly user: string
}

interface SelectOption {
  readonly value: string
  readonly label: string
}

export default function AdministratorPortfolioLimitAddPage() {
  return (
    <SuspenseWrapper>
      <PageContent />
    </SuspenseWrapper>
  )
}

function PageContent(): JSX.Element | null {
  const [successfullySubmited, setSuccessfullySubmited] = React.useState(false)
  const t = useTranslatable()
  const headers = useAuthenticatedHeaders()
  const navigate = useNavigate()
  const locale = useLocale()

  // Fetch sections for dropdown
  const { data: sections } = useApi({
    endpoint: Api.getAdministratorPortfolioListsSections,
    params: React.useMemo(() => ({ headers }), [headers]),
  })

  const sectionOptions = React.useMemo<SelectOption[]>(
    () => sections?.map((s) => ({ value: s.id.toString(), label: s.name })) ?? [],
    [sections]
  )

  const initialValues = React.useMemo<FormikValues>(
    () => ({
      sectionId: '',
      semester: '',
      allowed: '',
      user: '',
    }),
    []
  )

  const handleSubmit = React.useCallback(
    async (values: FormikValues): Promise<void> => {
      await Api.postAdministratorPortfolioLimits({
        headers,
        body: {
          sectionId: Number(values.sectionId),
          semester: Number(values.semester),
          profileId: Number(values.user.split('-')[0]),
          userId: Number(values.user.split('-')[1]),
          allowed: Number(values.allowed),
        },
      })
      toast.success(t('portfolio:submitted_successfully'))
      setSuccessfullySubmited(true)
    },
    [t, headers]
  )

  const redirectPath = React.useMemo(() => `/${locale}/administrator/portfolio/limits`, [locale])

  React.useEffect(() => {
    if (successfullySubmited) {
      navigate(redirectPath)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successfullySubmited])

  const breadcrumbsItems = React.useMemo(
    () => [
      { page: t('portfolio:portfolio'), path: `/${locale}/administrator/portfolio/sections` },
      { page: t('portfolio:limits'), path: `/${locale}/administrator/portfolio/limits` },
      { page: t('common:add'), path: `/${locale}/administrator/portfolio/limits/add` },
    ],
    [t, locale]
  )

  const sectionTitle = React.useMemo(
    () => `${t('portfolio:portfolio')} / ${t('portfolio:limits')} / ${t('common:add')}`,
    [t]
  )

  return (
    <Section title={sectionTitle} icon={<PortfolioIcon />} breadcrubms={breadcrumbsItems} rightElement={<BackButton />}>
      <Form
        initialValues={initialValues}
        enableReinitialize
        onSubmit={handleSubmit}
        classNames={{ form: 'w-full' }}
        isConfirmable={!successfullySubmited}
      >
        <FormContent sectionOptions={sectionOptions} />
      </Form>
    </Section>
  )
}

interface FormContentProps {
  readonly sectionOptions: SelectOption[]
}

function FormContent({ sectionOptions }: FormContentProps): JSX.Element {
  const t = useTranslatable()
  const headers = useAuthenticatedHeaders()
  const formik = useFormikContext<FormikValues>()
  const selectedSectionId = formik.values.sectionId
  const selectedSemester = formik.values.semester

  // Fetch student semesters based on selected section
  const { data: studentSemesters, isLoading: isLoadingStudentSemesters } = useApi({
    endpoint: Api.getAdministratorPortfolioListsStudentSemesters,
    params: React.useMemo(
      () => ({
        headers,
        query: { sectionId: selectedSectionId },
      }),
      [headers, selectedSectionId]
    ),
    shouldFetch: selectedSectionId !== '',
    suspense: false,
  })

  // Fetch students based on selected semester
  const { data: students, isLoading: isLoadingStudents } = useApi({
    endpoint: Api.getAdministratorPortfolioListsStudents,
    params: React.useMemo(
      () => ({
        headers,
        query: { semester: selectedSemester, sectionId: selectedSectionId },
      }),
      [headers, selectedSemester, selectedSectionId]
    ),
    shouldFetch: selectedSemester !== '',
    suspense: false,
  })

  const semesterOptions = React.useMemo<SelectOption[]>(
    () => studentSemesters?.map((s) => ({ value: s.id.toString(), label: s.name })) ?? [],
    [studentSemesters]
  )

  const studentOptions = React.useMemo<SelectOption[]>(
    () =>
      students?.map((s) => ({
        value: `${s.id}-${s.userId}`,
        label: `${s.name} (${s.persNum})`,
      })) ?? [],
    [students]
  )

  // Reset semester and user when section changes
  React.useEffect(() => {
    if (formik.values.semester !== '' || formik.values.user !== '') {
      void formik.setFieldValue('semester', '')
      void formik.setFieldValue('user', '')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSectionId])

  // Reset user when semester changes
  React.useEffect(() => {
    if (formik.values.user !== '') {
      void formik.setFieldValue('user', '')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSemester])

  return (
    <div className="mt-6 w-full rounded-card bg-card px-6 py-9">
      <FormError />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <SelectInput name="sectionId" label={t('portfolio:section')} options={sectionOptions} required />
        <SelectInput
          name="semester"
          label={t('common:semester')}
          options={semesterOptions}
          required
          disabled={selectedSectionId === ''}
          isLoading={isLoadingStudentSemesters}
        />
        <SelectInput
          name="user"
          label={t('student:student')}
          options={studentOptions}
          required
          disabled={selectedSemester === ''}
          isLoading={isLoadingStudents}
        />
        <TextInput name="allowed" type="number" label={t('portfolio:limit')} required />
      </div>
      <div className="mt-4 flex flex-wrap justify-end">
        <Button type="submit" variant="red" disabled={!formik.isValid || !formik.dirty}>
          {t('common:save')}
        </Button>
      </div>
    </div>
  )
}
